import { useCallback, useEffect, useRef, useState } from "react";

import { loadSession, type SessionResult } from "../auth";

export type SessionState = SessionResult | { kind: "loading" };

export function useSession(): readonly [SessionState, () => void] {
  const [result, setResult] = useState<SessionState>({ kind: "loading" });
  const activeRequest = useRef<AbortController | null>(null);

  const requestSession = useCallback((showLoading: boolean) => {
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;

    if (showLoading) {
      setResult({ kind: "loading" });
    }

    void loadSession(controller.signal)
      .then((nextResult) => {
        if (activeRequest.current === controller) {
          setResult(nextResult);
        }
      })
      .catch(() => undefined);
  }, []);

  const refresh = useCallback(() => requestSession(true), [requestSession]);

  useEffect(() => {
    const controller = new AbortController();
    activeRequest.current = controller;
    void loadSession(controller.signal)
      .then((nextResult) => {
        if (activeRequest.current === controller) {
          setResult(nextResult);
        }
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  return [result, refresh] as const;
}
